import { useCallback, useContext, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Toast } from '@m/alchemy-ui/Toast';
import { VotingType } from '../../../common/core-api';
import { CommunityContext } from '../../../context/Community';
import { type IdeaRow, MODERATION_STATUS } from '../IdeaCard.types';
import { computeTimeLeft, getCommonCardFlags } from '../IdeaCard.helpers';
import {
  FlaggedIndicator,
  IdeaAuthor,
  IdeaBody,
  IdeaFooter,
  IdeaLabels,
  IdeaThumbnail,
  IdeaTitle,
  MilestoneBadgeComponent,
  MilestoneExpiredBadge,
  TimeLeftCountdown,
  VoteActions
} from '../components';
import { useIdeaVoteState } from '../hooks/useIdeaVoteState';
import * as Styled from '../components/IdeaCard.styled';

interface MilestoneIdeaCardProps {
  readonly idea: IdeaRow;
  readonly isOwnProfile: boolean;
}

const getMilestoneCardFlags = (idea: IdeaRow, isOwnProfile: boolean, hideUnModerated: boolean) => {
  const challenge = idea.challenge;
  const common = getCommonCardFlags(idea, hideUnModerated);

  const isClosed = challenge?.access?.options?.showClosedEnabled ?? false;
  const votingEnabled = challenge?.access?.options?.voteOnIdeasEnabled ?? false;
  const walletEmpty =
    challenge?.voting?.wallet?.isEnabled && idea.viewerWalletAllowance === 0 && idea.viewerVoteDirection === null;
  const isPrivateMilestone = (() => {
    if (!idea.lastReachedMilestone || !challenge?.milestones) return false;
    const ms = challenge.milestones.find((m) => String(m.id) === String(idea.lastReachedMilestone));
    return ms?.isPrivateSubmission ?? false;
  })();

  return {
    ...common,
    isPrivate: common.isPrivate || isPrivateMilestone,
    isClosed,
    votingEnabled,
    walletEmpty,
    isOwnProfile
  };
};

export const MilestoneIdeaCard = ({ idea, isOwnProfile }: MilestoneIdeaCardProps) => {
  const intl = useIntl();
  const community = useContext(CommunityContext);
  const challenge = idea.challenge;

  const {
    voteCount,
    score,
    toastMessages,
    portalTarget,
    portalTargetRef,
    handleVoteSuccess: baseHandleVoteSuccess,
    handleVoteError
  } = useIdeaVoteState(idea.voteCount, idea.score);

  // Track if the final milestone was reached after a vote
  const [votingTargetReached, setVotingTargetReached] = useState(false);
  const handleVoteSuccess = useCallback(
    (result: { voteCount: number; score?: number; hasReachedFinalMilestone?: boolean }) => {
      baseHandleVoteSuccess(result);
      if (result.hasReachedFinalMilestone) {
        setVotingTargetReached(true);
      }
    },
    [baseHandleVoteSuccess]
  );

  const {
    isAnonymous,
    hasAccess,
    canModerate,
    isFlagged,
    isArchived,
    isClosed,
    votingEnabled,
    walletEmpty,
    isPrivate,
    isHidden
  } = getMilestoneCardFlags(idea, isOwnProfile, community.hideUnModerated === 1);

  // compare lastReachedMilestone with the last milestone in the challenge
  const hasReachedFinalMilestone = useMemo(() => {
    if (!idea.lastReachedMilestone || !challenge?.milestones?.length) return false;
    const finalMilestone = challenge.milestones[challenge.milestones.length - 1];
    return String(idea.lastReachedMilestone) === String(finalMilestone.id);
  }, [idea.lastReachedMilestone, challenge?.milestones]);

  const hideVoteButtons = isArchived || isClosed || hasReachedFinalMilestone || votingTargetReached;

  const isVotingDisabled =
    isOwnProfile ||
    hideVoteButtons ||
    walletEmpty ||
    !votingEnabled ||
    idea.moderationStatus === MODERATION_STATUS.NOT_APPROVED ||
    hasReachedFinalMilestone ||
    votingTargetReached ||
    !(idea.viewerPermissions?.canVoteOnMilestone ?? false);

  // Find milestones from challenge data
  const milestones = challenge?.milestones;
  const currentMilestoneId = idea.currentMilestone;
  const lastReachedMilestoneId = idea.lastReachedMilestone;
  const daysLimit = challenge?.daysLimit;
  const createdAt = idea.createdAt;

  const currentMilestoneData = useMemo(() => {
    if (!currentMilestoneId || !milestones) return null;
    return milestones.find((m) => String(m.id) === String(currentMilestoneId)) ?? null;
  }, [currentMilestoneId, milestones]);

  const lastReachedMilestoneData = useMemo(() => {
    if (!lastReachedMilestoneId || !milestones) return null;
    return milestones.find((m) => String(m.id) === String(lastReachedMilestoneId)) ?? null;
  }, [lastReachedMilestoneId, milestones]);

  const timeLeft = useMemo(() => {
    if (!daysLimit) return null;
    return computeTimeLeft(createdAt, daysLimit);
  }, [daysLimit, createdAt]);

  // "Expired" badge
  const isExpired = idea.moderationStatus === MODERATION_STATUS.NOT_APPROVED && timeLeft !== null && !timeLeft.ongoing;

  const showMilestoneBadge = currentMilestoneData && idea.moderationStatus !== MODERATION_STATUS.NOT_APPROVED;

  // Show "Not Approved" badge
  const showNotApprovedBadge = !isExpired && idea.moderationStatus === MODERATION_STATUS.NOT_APPROVED;

  const effectiveVotingType = useMemo(() => {
    if (currentMilestoneData?.condition?.type === 'score') {
      const milestoneVotingType = currentMilestoneData.voting?.type;
      if (milestoneVotingType === VotingType.SCORECARD) return VotingType.SCORECARD;
      return VotingType.UP;
    }
    return challenge?.voting?.type ?? null;
  }, [currentMilestoneData, challenge?.voting?.type]);

  const isLastMilestoneScore = lastReachedMilestoneData?.condition?.type === 'score';
  const votingClosed = isArchived || isClosed;
  const voteLabel =
    (votingClosed || hasReachedFinalMilestone || votingTargetReached) && isLastMilestoneScore
      ? intl.formatMessage({ id: 'votesAchieved', defaultMessage: 'Votes Achieved' })
      : undefined;
  const showThumbsUp = isLastMilestoneScore;
  const hasTopBadge = !!(showMilestoneBadge && lastReachedMilestoneData) || isExpired || showNotApprovedBadge;

  return (
    <>
      <Styled.CardWrapper $hasTopBadge={hasTopBadge}>
        {isFlagged && (isOwnProfile || canModerate) && <FlaggedIndicator />}

        {showMilestoneBadge && lastReachedMilestoneData && (
          <MilestoneBadgeComponent
            lastReachedMilestone={lastReachedMilestoneData}
            currentMilestone={currentMilestoneData}
          />
        )}
        {isExpired && <MilestoneExpiredBadge isExpired />}
        {showNotApprovedBadge && <MilestoneExpiredBadge isExpired={false} />}

        {idea.imageFile && (
          <IdeaThumbnail imageFile={idea.imageFile} ideaId={idea.id} hasAccess={hasAccess} hasTopBadge={hasTopBadge} />
        )}

        <Styled.ContentWrapper $hasTopBadge={hasTopBadge}>
          <IdeaLabels
            moderationStatus={idea.moderationStatus}
            statusLabels={idea.statusLabels}
            isHidden={isHidden}
            isPrivate={isPrivate}
          />

          <IdeaTitle ideaId={idea.id} title={idea.title} hasAccess={hasAccess} />
          <IdeaBody ideaId={idea.id} text={idea.text} hasAccess={hasAccess} />

          <IdeaFooter
            voteCount={voteCount}
            score={score}
            commentCount={idea.commentCount ?? 0}
            votingType={effectiveVotingType}
            isBlindVoting={challenge?.voting?.isBlind ?? false}
            canModerate={canModerate}
            voteLabel={voteLabel}
            showThumbsUp={showThumbsUp}
            maxStars={challenge?.voting?.options?.maxStars ?? undefined}
            prefix={timeLeft && challenge?.daysLimit ? <TimeLeftCountdown timeLeft={timeLeft} /> : undefined}
            suffix={
              <>
                {hideVoteButtons && (
                  <Styled.VotingClosed>
                    <FormattedMessage id="votingClosed" defaultMessage="Voting closed" />
                  </Styled.VotingClosed>
                )}
                {!isOwnProfile && hasAccess && !hideVoteButtons && (
                  <VoteActions
                    ideaId={idea.id}
                    viewerVoteDirection={idea.viewerVoteDirection}
                    votingType={effectiveVotingType}
                    votingOptions={challenge?.voting?.options}
                    disabled={isVotingDisabled}
                    onVoteSuccess={handleVoteSuccess}
                    onVoteError={handleVoteError}
                  />
                )}
              </>
            }
          />
        </Styled.ContentWrapper>

        <IdeaAuthor
          ideaId={idea.id}
          user={idea.user}
          isAnonymous={isAnonymous}
          createdAt={idea.createdAt}
          hasTopBadge={hasTopBadge}
        />
      </Styled.CardWrapper>
      <div ref={portalTargetRef} style={{ position: 'relative' }}>
        {/* @ts-expect-error - Toast portalTarget typing mismatch */}
        <Toast messages={toastMessages} portalTarget={portalTarget} />
      </div>
    </>
  );
};
